/**
 * WarehouseInboundPage — origin-warehouse operator (e.g. Guangzhou, Istanbul)
 * sees every participant carton arriving at their facility, then confirms
 * physical receipt or rejects it (damaged / wrong sender / etc.).
 *
 * Mirrors Flutter `warehouses/intake_page.dart`. Required capability:
 * `origin_warehouse_operator`. International shipping companies operating
 * their own warehouse get this granted at the company level.
 *
 * Two tabs:
 *   - بانتظار الاستلام (status = awaiting_warehouse OR sender uploaded
 *     a dispatch proof but operator hasn't confirmed yet)
 *   - استُلمت (status = at_origin_warehouse)
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Building2, Package, CheckCircle, XCircle, Loader, Hash,
  Weight, ExternalLink, AlertCircle, Image as ImageIcon,
} from 'lucide-react';
import { useMyContainers, useContainerParticipants, useConfirmWarehouse, useRejectWarehouse } from '@/hooks/useContainers';
import { AppLayout } from '@/components/layout/AppLayout';
import { Button } from '@/components/common/Button';
import { TextField } from '@/components/common/TextField';
import type { ContainerParticipant } from '@/api/containers';

type Tab = 'awaiting' | 'received';

export default function WarehouseInboundPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>('awaiting');
  const [selectedContainerId, setSelectedContainerId] = useState<number | null>(null);
  const [actionTarget, setActionTarget] = useState<ContainerParticipant | null>(null);

  // Operator's containers — filter to those still receiving goods at the
  // warehouse (post-publish, pre-seal). After `sealed`, the container is
  // closed for warehouse intake.
  const myContainersQ = useMyContainers();
  const openContainers = (myContainersQ.data || []).filter(
    (c) => ['open_for_joins', 'joins_closed', 'consolidating'].includes(c.status),
  );

  // Default to first container if none picked.
  const cId = selectedContainerId ?? openContainers[0]?.id ?? null;
  const participantsQ = useContainerParticipants(cId);

  const groups = useMemo(() => {
    const all = participantsQ.data || [];
    return {
      // "Awaiting": approved by organizer but not yet physically received.
      // pending_warehouse_confirmation = sender uploaded dispatch proof,
      // operator just needs to confirm intake.
      awaiting: all.filter((p) => ['approved', 'awaiting_supplier_dispatch', 'pending_warehouse_confirmation'].includes(p.status)),
      received: all.filter((p) => p.status === 'at_origin_warehouse'),
    };
  }, [participantsQ.data]);

  const list = groups[tab];

  return (
    <AppLayout>
      <h1 className="text-xl font-bold text-text-primary mb-3 flex items-center gap-2">
        <Building2 size={20} /> {t('warehouse_inbound', 'استقبال شحنات المستودع')}
      </h1>

      {/* Container picker */}
      {myContainersQ.isLoading ? (
        <div className="bg-surface border border-border rounded-xl p-6 text-center">
          <Loader size={28} className="animate-spin mx-auto text-text-tertiary" />
        </div>
      ) : openContainers.length === 0 ? (
        <div className="bg-surface border border-border rounded-xl p-10 text-center">
          <Building2 size={40} className="mx-auto text-text-tertiary mb-3" />
          <p className="text-text-secondary">
            {t('no_active_containers', 'لا توجد حاويات مفتوحة الآن')}
          </p>
        </div>
      ) : (
        <>
          <div className="flex gap-2 overflow-x-auto pb-2 mb-4">
            {openContainers.map((c) => (
              <button
                key={c.id}
                onClick={() => setSelectedContainerId(c.id)}
                className={`whitespace-nowrap rounded-xl px-4 py-2 text-sm border transition ${
                  cId === c.id ? 'bg-primary text-white border-primary' : 'bg-surface text-text-secondary border-border hover:border-primary'
                }`}
              >
                <Hash size={12} className="inline" /> {c.public_code}
              </button>
            ))}
          </div>

          {/* Tabs */}
          <div className="flex gap-2 border-b border-border mb-4">
            {([
              { key: 'awaiting', label: t('awaiting_receipt', 'بانتظار الاستلام'), count: groups.awaiting.length },
              { key: 'received', label: t('received', 'استُلمت'),                  count: groups.received.length },
            ] as const).map((tabItem) => (
              <button
                key={tabItem.key}
                onClick={() => setTab(tabItem.key)}
                className={`px-4 py-2 -mb-px border-b-2 font-medium text-sm ${
                  tab === tabItem.key ? 'border-primary text-primary' : 'border-transparent text-text-secondary'
                }`}
              >
                {tabItem.label}
                {tabItem.count > 0 && (
                  <span className="ms-1.5 inline-block bg-primary/10 text-primary text-xs rounded-full px-2">
                    {tabItem.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Participants list */}
          {participantsQ.isLoading ? (
            <div className="flex justify-center py-12 text-text-tertiary">
              <Loader size={28} className="animate-spin" />
            </div>
          ) : list.length === 0 ? (
            <div className="bg-surface border border-border rounded-xl p-10 text-center">
              <Package size={40} className="mx-auto text-text-tertiary mb-3" />
              <p className="text-text-secondary">
                {tab === 'awaiting' ? t('no_awaiting_cartons', 'لا شحنات بانتظار الاستلام') : t('no_received_cartons', 'لا شحنات مُستلَمة')}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {list.map((p) => (
                <ParticipantCard
                  key={p.id}
                  participant={p}
                  showActions={tab === 'awaiting'}
                  onAction={() => setActionTarget(p)}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* Confirm/Reject sheet */}
      {actionTarget && cId && (
        <IntakeSheet
          containerId={cId}
          participant={actionTarget}
          onClose={() => setActionTarget(null)}
        />
      )}
    </AppLayout>
  );
}

function ParticipantCard({ participant: p, showActions, onAction }: {
  participant: ContainerParticipant;
  showActions: boolean;
  onAction: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="bg-surface border border-border rounded-xl p-4">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div>
          <div className="font-mono text-sm text-primary flex items-center gap-1">
            <Hash size={12} /> {p.public_code}
          </div>
          {p.title && <div className="text-sm text-text-secondary">{p.title}</div>}
        </div>
        {p.dispatch_proof_url && (
          <a
            href={p.dispatch_proof_url}
            target="_blank"
            rel="noreferrer"
            className="text-primary text-xs flex items-center gap-1 hover:underline"
          >
            <ImageIcon size={12} /> {t('view_proof', 'إثبات الإرسال')} <ExternalLink size={10} />
          </a>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2 text-sm text-text-secondary mb-3">
        {p.est_weight_kg != null && (
          <span className="flex items-center gap-1"><Weight size={12} /> {p.est_weight_kg} كغ</span>
        )}
        {p.supplier_invoice_number && (
          <span className="flex items-center gap-1 font-mono text-xs" dir="ltr">
            <Hash size={11} /> {p.supplier_invoice_number}
          </span>
        )}
        {p.declared_value_usd != null && (
          <span>{t('declared', 'القيمة المعلنة')}: ${p.declared_value_usd}</span>
        )}
      </div>
      {showActions && (
        <div className="flex gap-2">
          <Button onClick={onAction} className="flex-1">
            <CheckCircle size={14} /> {t('confirm_receipt', 'تأكيد الاستلام')}
          </Button>
        </div>
      )}
    </div>
  );
}

function IntakeSheet({ containerId, participant, onClose }: {
  containerId: number; participant: ContainerParticipant; onClose: () => void;
}) {
  const { t } = useTranslation();
  const confirmMut = useConfirmWarehouse();
  const rejectMut = useRejectWarehouse();
  const [tab, setTab] = useState<'confirm' | 'reject'>('confirm');
  const [actualWeight, setActualWeight] = useState(String(participant.est_weight_kg ?? ''));
  const [photoUrl, setPhotoUrl] = useState('');
  const [notes, setNotes] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function doConfirm() {
    setError(null);
    try {
      const w = parseFloat(actualWeight);
      await confirmMut.mutateAsync({
        containerId,
        pid: participant.id,
        actualWeightKg: Number.isFinite(w) ? w : undefined,
        photos: photoUrl.trim() ? [photoUrl.trim()] : undefined,
        note: notes.trim() || undefined,
      });
      onClose();
    } catch (e: any) {
      setError(e?.message || 'فشل التأكيد');
    }
  }
  async function doReject() {
    setError(null);
    if (!reason.trim()) {
      setError(t('reject_reason_required', 'سبب الرفض مطلوب'));
      return;
    }
    try {
      await rejectMut.mutateAsync({ containerId, pid: participant.id, reason: reason.trim() });
      onClose();
    } catch (e: any) {
      setError(e?.message || 'فشل الرفض');
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50" onClick={onClose}>
      <div
        className="bg-surface rounded-t-2xl sm:rounded-2xl p-6 max-w-md w-full max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-text-primary">
            {t('intake_sheet_title', 'استلام شحنة')} <span className="text-primary font-mono">{participant.public_code}</span>
          </h3>
          <button onClick={onClose} className="p-1 text-text-tertiary hover:text-text-primary">×</button>
        </div>

        <div className="flex gap-2 border-b border-border mb-4">
          <button
            onClick={() => setTab('confirm')}
            className={`px-3 py-2 -mb-px border-b-2 text-sm font-medium ${
              tab === 'confirm' ? 'border-success text-success' : 'border-transparent text-text-secondary'
            }`}
          >
            {t('confirm', 'تأكيد')}
          </button>
          <button
            onClick={() => setTab('reject')}
            className={`px-3 py-2 -mb-px border-b-2 text-sm font-medium ${
              tab === 'reject' ? 'border-error text-error' : 'border-transparent text-text-secondary'
            }`}
          >
            {t('reject', 'رفض')}
          </button>
        </div>

        {error && (
          <div className="bg-error/10 text-error rounded-xl p-3 text-sm mb-3 flex items-center gap-2">
            <AlertCircle size={14} /> {error}
          </div>
        )}

        {tab === 'confirm' ? (
          <div className="space-y-3">
            <p className="text-sm text-text-secondary">
              {t('intake_confirm_hint', 'استلمت الكرتون فعلياً، وزنته، وصوّرته. هذه الخطوة تُعلِم المرسِل أنّ بضاعته جاهزة في المستودع.')}
            </p>
            <TextField
              label={t('actual_weight_kg', 'الوزن الفعلي (كغ)')}
              type="number"
              step="0.1"
              dir="ltr"
              value={actualWeight}
              onChange={(e) => setActualWeight(e.target.value)}
            />
            <TextField
              label={t('intake_photo_url', 'رابط صورة الاستلام')}
              dir="ltr"
              placeholder="https://…"
              value={photoUrl}
              onChange={(e) => setPhotoUrl(e.target.value)}
            />
            <TextField
              label={t('intake_notes', 'ملاحظات (اختيارية)')}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
            <Button onClick={doConfirm} loading={confirmMut.isPending} fullWidth>
              <CheckCircle size={16} /> {t('confirm_receipt', 'تأكيد الاستلام')}
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-text-secondary">
              {t('intake_reject_hint', 'الرفض يُبلِغ المرسِل أنّ بضاعته لم تُستلَم (تالفة، خاطئة، إلخ). يبقى عليه إعادة الإرسال.')}
            </p>
            <TextField
              label={t('reject_reason', 'سبب الرفض')}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('reject_reason_placeholder', 'مثال: الكرتون متضرّر، أو البضاعة لا تطابق الوصف') as string}
            />
            <Button
              onClick={doReject}
              loading={rejectMut.isPending}
              className="!bg-error hover:!bg-error/90"
              fullWidth
            >
              <XCircle size={16} /> {t('reject_carton', 'رفض الاستلام')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
